import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiFilter, FiUser, FiCheck, FiFileText, FiActivity, FiShield, FiDownload } from "react-icons/fi";
import { FaStethoscope, FaReceipt } from "react-icons/fa";
import LeftMenu from '../components/LeftMenu';
import axios from 'axios';
import { BASE_URL, GET_PENDING_BILLS, POST_PAYMENT, GET_INVOICE_DETAILS } from '../utlilities/config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import { TbRefresh } from "react-icons/tb";
import PaymentModal from '../components/PaymentModal';
import { getWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';
import { webPrintA5Receipt, webPrintElementById } from '../utlilities/webPrint';
import { formatBillDateTime, getBillCopies, invokeBillPrint } from '../utlilities/billPrint';
import { toPng } from 'html-to-image';
import ReceiptNormalComponent from '../components/NormalReciept';
import ReceiptKotComponent from '../components/KotReciept';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Spinner = () => (
  <div className="fixed inset-0 z-[3200] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const formatPrintInvoiceNumber = (invoiceNo) => {
  const rawValue = invoiceNo ?? '';
  const rawString = String(rawValue).trim();

  if (!rawString) return '';
  if (rawString.includes('/')) return rawString;

  const parsedInvoice = Number.parseInt(rawString, 10);
  const invoicePart = Number.isNaN(parsedInvoice)
    ? rawString
    : String(parsedInvoice).padStart(2, '0');

  const seriesName = String(localStorage.getItem('series_name') || '').trim();
  return seriesName ? `${invoicePart}/${seriesName}` : invoicePart;
};

const formatBillEntryDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const numberToWords = (num) => {
  if (num === 0) return "Zero Rupees Only";

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertHundreds = (n) => {
    if (n === 0) return '';
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
      if (n > 0) result += 'and ';
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result.trim();
  };

  const numStr = parseFloat(num).toFixed(2);
  const parts = numStr.split('.');
  const rupees = parseInt(parts[0]);
  const paise = parseInt(parts[1] || 0);

  let words = '';

  if (rupees === 0) {
    words = 'Zero';
  } else if (rupees < 1000) {
    words = convertHundreds(rupees);
  } else if (rupees < 100000) {
    const thousands = Math.floor(rupees / 1000);
    const remainder = rupees % 1000;
    words = convertHundreds(thousands) + ' Thousand';
    if (remainder > 0) words += ' ' + convertHundreds(remainder);
  } else if (rupees < 10000000) {
    const lakhs = Math.floor(rupees / 100000);
    const remainder = rupees % 100000;
    words = convertHundreds(lakhs) + ' Lakh';
    if (remainder > 0) {
      if (remainder >= 1000) {
        const thousands = Math.floor(remainder / 1000);
        const remainder2 = remainder % 1000;
        words += ' ' + convertHundreds(thousands) + ' Thousand';
        if (remainder2 > 0) words += ' ' + convertHundreds(remainder2);
      } else {
        words += ' ' + convertHundreds(remainder);
      }
    }
  } else {
    const crores = Math.floor(rupees / 10000000);
    const remainder = rupees % 10000000;
    words = convertHundreds(crores) + ' Crore';
    if (remainder > 0) {
      if (remainder >= 100000) {
        const lakhs = Math.floor(remainder / 100000);
        const remainder2 = remainder % 100000;
        words += ' ' + convertHundreds(lakhs) + ' Lakh';
        if (remainder2 > 0) {
          if (remainder2 >= 1000) {
            const thousands = Math.floor(remainder2 / 1000);
            const remainder3 = remainder2 % 1000;
            words += ' ' + convertHundreds(thousands) + ' Thousand';
            if (remainder3 > 0) words += ' ' + convertHundreds(remainder3);
          } else {
            words += ' ' + convertHundreds(remainder2);
          }
        }
      } else if (remainder >= 1000) {
        const thousands = Math.floor(remainder / 1000);
        const remainder2 = remainder % 1000;
        words += ' ' + convertHundreds(thousands) + ' Thousand';
        if (remainder2 > 0) words += ' ' + convertHundreds(remainder2);
      } else {
        words += ' ' + convertHundreds(remainder);
      }
    }
  }

  words = words.trim() + ' Rupees';
  if (paise > 0) {
    words += ' and ' + convertHundreds(paise).trim() + ' Paise';
  }

  return words + ' Only';
};

const roundPrintAmount = (total) => {
  const value = parseFloat(total || 0);
  if ((value - Math.floor(value)) < 0.5) {
    return Math.floor(value);
  }
  return Math.ceil(value);
};

const formatBillPrintDate = formatBillDateTime;

const transformInvoiceItems = (details, itemList) => {
  const itemListMap = {};
  if (itemList && Array.isArray(itemList)) {
    itemList.forEach((item) => {
      itemListMap[item.Item_No] = item;
    });
  }

  return details.map((detail) => {
    const itemInfo = itemListMap[detail.ItemNo];

    return {
      SI_No: detail.SI_No || 0,
      ItemNo: detail.ItemNo,
      ItemName: detail.ItemName || (itemInfo ? itemInfo.Item_Name : ''),
      BatchCode: detail.BatchCode || '',
      Qty: parseFloat(detail.Qty || 0),
      SalesPrice: parseFloat(detail.SalesPrice || 0),
      MRP: parseFloat(detail.MRP || 0),
      Disc_Percentage: parseFloat(detail.Disc_Percentage || 0),
      Disc_Amount: parseFloat(detail.Disc_Amount || 0),
      TaxAmount: parseFloat(detail.TaxAmount || 0),
      NetTotal: parseFloat(detail.NetTotal || 0),
      PostTaxTotal: parseFloat(detail.PostTaxTotal || detail.Total || 0),
      Total: parseFloat(detail.Total || 0),
       TaxCode: detail.TaxCode || 0,
      ExpiryDate: itemInfo?.ItemBatch ? (() => {
        try {
          const batches = typeof itemInfo.ItemBatch === 'string'
            ? JSON.parse(itemInfo.ItemBatch)
            : itemInfo.ItemBatch;
          const batch = batches.find((b) => b.BatchCode === detail.BatchCode);
          return batch?.ExpiryDate || '';
        } catch {
          return '';
        }
      })() : ''
    };
  });
};

const PaymentHandlingPage = () => {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');

  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchBy, setSearchBy] = useState('department');
  const [opNumberSearch, setOpNumberSearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [selectedBills, setSelectedBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [printAfterSave, setPrintAfterSave] = useState(true);
  const [printBillAfterSave, setPrintBillAfterSave] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);

  const [sysInfo, setSysInfo] = useState({})
  const [clientInfo, setClientInfo] = useState({})
  const [generalsettings, setGeneralSettings] = useState({});
  const [userInfo, setUserInfo] = useState({})

  const searchOpnumRef = useRef(null);

  // Barcode Scanner States
  const [scannedBarcodes, setScannedBarcodes] = useState([]);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [scanBuffer, setScanBuffer] = useState('');
  const scanTimerRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const isScanningRef = useRef(false);
  const exportMenuRef = useRef(null);

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
      ipcRenderer = window.require('electron').ipcRenderer;
  }

  const [allBills, setAllBills] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowDownloadOptions(false);
      }
    };
    if (showDownloadOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadOptions]);

  useEffect(() => {
    //searchOpnumRef.current?.focus();
    getData();
  }, []);


  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      let sysinfo;
      let generalSetting;
      let client_info;
      let userinfo;

      if(isElectron()){
        userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
        sysinfo = await ipcRenderer.invoke('store-get', 'sysInfo');
        client_info = await ipcRenderer.invoke('store-get', 'clientInfo');
        
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
      } else {
        initializeWebUserInfo();
        const webInfo = getWebUserInfo();
        userinfo = webInfo.userInfo;
        sysinfo = webInfo.sysInfo;
        generalSetting =
          webInfo.generalOpSettings ||
          webInfo.generalopsettings ||
          webInfo.generalSettings;
        client_info = webInfo.clientInfo;
      }

      console.log('hereee sysss', sysinfo)
      
      setSysInfo(sysinfo);
      setGeneralSettings(generalSetting);
      setClientInfo(client_info);
      setUserInfo(userinfo);;

      if(generalSetting?.AutoRecieptPrint){
        setPrintAfterSave(true)
      } else  {
        setPrintAfterSave(false)
      }

      setPrintBillAfterSave(!!generalSetting?.billPrintonlyPaymentsave);
    };

    fetchData();
  }, []);

  // Barcode Scanner Effect
  useEffect(() => {
    const handleBarcodeInput = (e) => {
      // Ignore if typing in input fields or if modals are open
      if (e.target.tagName === 'INPUT' || showPaymentModal) {
        return;
      }

      const currentTime = Date.now();
      const timeDiff = currentTime - lastScanTimeRef.current;

      // Barcode scanners type very fast (usually < 50ms between characters)
      // Manual typing is usually > 100ms
      if (timeDiff > 100) {
        // Reset buffer if too much time has passed (likely manual typing)
        setScanBuffer('');
        isScanningRef.current = false;
      }

      lastScanTimeRef.current = currentTime;

      // Clear existing timer
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current);
      }

      if (e.key === 'Enter' && scanBuffer.length > 0) {
        // Barcode scan complete
        e.preventDefault(); // Prevent this Enter from triggering other handlers
        const scannedValue = scanBuffer.trim();
        if (scannedValue) {
          isScanningRef.current = true; // Mark that we're in scanning mode
          // Replace previous scan — only one bill can be selected at a time
          setScannedBarcodes([scannedValue]);
          setShowScanModal(true);
          toast.success(`Scanned: ${scannedValue}`, { autoClose: 1000 });
          
          // Reset scanning flag after a short delay
          setTimeout(() => {
            isScanningRef.current = false;
          }, 200);
        }
        setScanBuffer('');
      } else if (e.key.length === 1) {
        // Add character to buffer (only printable characters)
        setScanBuffer(prev => prev + e.key);
        isScanningRef.current = true; // We're actively scanning

        // Auto-reset buffer after 100ms of no input
        scanTimerRef.current = setTimeout(() => {
          setScanBuffer('');
          isScanningRef.current = false;
        }, 100);
      }
    };

    document.addEventListener('keydown', handleBarcodeInput);

    return () => {
      document.removeEventListener('keydown', handleBarcodeInput);
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current);
      }
    };
  }, [scanBuffer, showPaymentModal]);

  // Function to remove a scanned barcode
  const removeScannedBarcode = (barcode) => {
    setScannedBarcodes(prev => prev.filter(b => b !== barcode));
  };

  // Function to clear all scanned barcodes (but keep modal open)
  const clearAllScannedBarcodes = () => {
    setScannedBarcodes([]);
  };

  // Function to close scan modal and clear barcodes
  const closeScanModal = () => {
    setScannedBarcodes([]);
    setShowScanModal(false);
  };

  // Function to load scanned barcodes and select matching bills
  const loadScannedBarcodes = useCallback(() => {
    if (scannedBarcodes.length === 0) {
      toast.warning('No barcodes scanned');
      return;
    }

    // Find bills matching the scanned invoice numbers
    const matchedBills = allBills.filter(bill => 
      scannedBarcodes.includes(bill.invoiceNo.toString())
    );

    if (matchedBills.length === 0) {
      toast.error('No matching bills found for scanned barcodes');
      return;
    }

    if (matchedBills.length > 1) {
      toast.info('Only one bill can be selected. Using the first match.');
    }

    const bill = matchedBills[0];
    setSelectedBills([bill.id]);
    setPaymentAmounts({ [bill.id]: bill.amount });

    toast.success('Bill selected from scan');
    
    // Clear scanned barcodes and close scan modal
    setScannedBarcodes([]);
    setShowScanModal(false);
    
    // Automatically open payment modal
    setTimeout(() => {
      setShowPaymentModal(true);
    }, 300); // Small delay to ensure state updates complete
  }, [scannedBarcodes, allBills]);

  // Handle Enter key press in scan modal (for manual Enter, not from scanner)
  useEffect(() => {
    const handleKeysInScanModal = (e) => {
      if (!showScanModal) return;

      // Handle Escape key to close modal
      if (e.key === 'Escape') {
        e.preventDefault();
        closeScanModal();
        return;
      }

      // Handle Enter key to load bills
      // Only trigger if:
      // 1. Scan modal is open
      // 2. Enter key is pressed
      // 3. There are scanned barcodes
      // 4. NOT currently in the middle of scanning (to allow multiple scans)
      if (e.key === 'Enter' && scannedBarcodes.length > 0 && !isScanningRef.current) {
        e.preventDefault();
        loadScannedBarcodes();
      }
    };

    if (showScanModal) {
      document.addEventListener('keydown', handleKeysInScanModal, { capture: false });
    }

    return () => {
      document.removeEventListener('keydown', handleKeysInScanModal, { capture: false });
    };
  }, [showScanModal, scannedBarcodes, loadScannedBarcodes]);

  const getExportRows = () =>
    filteredBills.map((bill, index) => ({
      'Sl No': index + 1,
      'Patient Name': bill.patientName || '',
      'OP / Invoice': bill.opNumber || '',
      'Invoice No': bill.invoiceNo ?? '',
      Department: bill.department || '',
      'Entry Date': bill.entryDate
        ? String(bill.entryDate).includes('T')
          ? String(bill.entryDate).split('T')[0]
          : String(bill.entryDate)
        : '',
      'Total Amount': Number(bill.totalAmount || 0),
      'Paid Amount': Number(bill.paidAmount || 0),
      'Balance Due': Number(bill.amount || 0),
      Mobile: bill.mobileNo || '',
    }));

  const exportToExcel = () => {
    if (!filteredBills.length) {
      toast.error('No pending bills to export', { autoClose: 1500 });
      return;
    }

    const rows = getExportRows();
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);

    const totalDue = filteredBills.reduce((sum, bill) => sum + Number(bill.amount || 0), 0);
    XLSX.utils.sheet_add_aoa(ws, [['', '']], { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, [['Summary', '']], { origin: -1 });
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ['From Date', fromDate],
        ['To Date', selectedDate],
        ['Total Bills', filteredBills.length],
        ['Total Balance Due', totalDue],
      ],
      { origin: -1 }
    );

    XLSX.utils.book_append_sheet(wb, ws, 'Pending Bills');
    XLSX.writeFile(wb, `Pending_Bills_${fromDate}_to_${selectedDate}.xlsx`);
    toast.success('Excel exported successfully', { autoClose: 1500 });
  };

  const exportToPDF = () => {
    if (!filteredBills.length) {
      toast.error('No pending bills to export', { autoClose: 1500 });
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    const totalDue = filteredBills.reduce((sum, bill) => sum + Number(bill.amount || 0), 0);

    doc.setFontSize(14);
    doc.text('Pending Bills - Payment Management', 14, 14);
    doc.setFontSize(9);
    doc.text(`Date Range: ${fromDate} to ${selectedDate}`, 14, 21);
    doc.text(`Bills: ${filteredBills.length}`, 14, 27);
    doc.text(`Balance Due: Rs. ${Number(totalDue).toFixed(2)}`, 80, 27);

    const rows = getExportRows();
    autoTable(doc, {
      startY: 34,
      head: [[
        'Sl',
        'Patient',
        'OP / Invoice',
        'Inv No',
        'Department',
        'Entry Date',
        'Bill Amt',
        'Paid',
        'Due',
        'Mobile',
      ]],
      body: rows.map((row) => [
        row['Sl No'],
        row['Patient Name'],
        row['OP / Invoice'],
        row['Invoice No'],
        row.Department,
        row['Entry Date'],
        Number(row['Total Amount']).toFixed(2),
        Number(row['Paid Amount']).toFixed(2),
        Number(row['Balance Due']).toFixed(2),
        row.Mobile,
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
      theme: 'grid',
    });

    doc.save(`Pending_Bills_${fromDate}_to_${selectedDate}.pdf`);
    toast.success('PDF exported successfully', { autoClose: 1500 });
  };

  const handleDownload = (formatType) => {
    setShowDownloadOptions(false);
    if (formatType === 'excel') {
      exportToExcel();
    } else {
      exportToPDF();
    }
  };

  const getData = async (date = selectedDate, from = fromDate) => {
    setSelectedDepartment('all')
    setSelectedBills([])
    setFilteredBills([])
    try {  
      setLoading(true);

      const response = await axios.get(`${BASE_URL}${GET_PENDING_BILLS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'EntryDate': date,
          'FromDate': from,
          'opNumber': '',
        },
      });

      if(response.status === 200){
        processApiData(response.data.data);
      }

    } catch (error) {
      console.log('Error fetching data:', error);
      toast.error('Error fetching data!', {
        autoClose: 2000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    // Keep FromDate from going past ToDate
    const nextFrom = fromDate && fromDate > newDate ? newDate : fromDate;
    if (nextFrom !== fromDate) {
      setFromDate(nextFrom);
    }
    getData(newDate, nextFrom);
  };

  const handleFromDateChange = (e) => {
    const newFrom = e.target.value;
    setFromDate(newFrom);
    // Keep ToDate from going before FromDate
    const nextTo = selectedDate && selectedDate < newFrom ? newFrom : selectedDate;
    if (nextTo !== selectedDate) {
      setSelectedDate(nextTo);
    }
    // Close the native date picker after selection
    e.target.blur();
    getData(nextTo, newFrom);
  };

  const processApiData = (data) => {
    // Process bills from API response - Only show Pharmacy bills
    const processedBills = [];
    const billIdSet = new Set(); // Track unique bill IDs to prevent duplicates
    const departmentCounts = new Map();
    
    data.forEach(departmentData => {
      const departmentName = (departmentData.department || '').trim(); // Use department from parent group
      
      // Filter to only include Pharmacy department bills (case-insensitive)
      const isPharmacyDepartment = departmentName && 
        (departmentName.toLowerCase().includes('pharmacy') || 
         departmentName.toLowerCase() === 'pharmacy');
      
      if (!isPharmacyDepartment) {
        return; // Skip non-pharmacy departments
      }
      
      departmentData.bills.forEach(bill => {
        // Create unique ID using invoiceNo, transType, and sequence to handle same bill in different departments
        const uniqueId = `${bill.invoiceNo}-${bill.transType}-${bill.sequence || 0}`;
        
        // Skip if this exact bill ID already exists (prevent duplicates)
        if (billIdSet.has(uniqueId)) {
          console.warn(`Duplicate bill detected: ${uniqueId} in department ${departmentName}`);
          return;
        }
        
        billIdSet.add(uniqueId);
        
        processedBills.push({
          id: uniqueId,
          patientName: bill.patientName,
          department: departmentName, // Use department from departmentData, not from bill
          amount: bill.balanceDue,
          totalAmount: bill.totalAmount,
          paidAmount: bill.paidAmount || 0,
          invoiceNo: bill.invoiceNo,
          opNumber: `${bill.transType}${bill.sequence}/${bill.invoiceNo}`, // Create OP number format
          patientId: bill.patientID,
          transType: bill.transType,
          entryDate: bill.entryDate,
          createdAt: bill.createdAt,
          lastUpdatedAt: bill.lastUpdatedAt,
          docStatus: bill.docStatus,
          editNo: bill.editNo,
          sequence: bill.sequence,
          clientID: bill.clientID,
          mobileNo: bill.mobileNo,
          receiptHistory: bill.receiptHistory || []
        });
      });

      if (departmentData.bills?.length) {
        departmentCounts.set(
          departmentName,
          (departmentCounts.get(departmentName) || 0) + departmentData.bills.length
        );
      }
    });
  
    setAllBills(processedBills);

    const departmentMeta = Array.from(departmentCounts.entries()).map(([name, count]) => {
      const normalizedName = name.toLowerCase();
      const isIp = normalizedName.includes('ip');

      return {
        id: name,
        name,
        count,
        icon: isIp ? FaStethoscope : FaReceipt,
        bgColor: isIp ? 'bg-blue-50' : 'bg-green-50',
        textColor: isIp ? 'text-blue-700' : 'text-green-700'
      };
    });

    setDepartments(departmentMeta);
  };

  
  useEffect(() => {
    let filtered = [...allBills];

    if (searchBy === 'department') {
      // Filter by department
      if (selectedDepartment !== 'all') {
        filtered = filtered.filter(bill => bill.department === selectedDepartment);
      }
      
      // Filter by department search
      if (departmentSearch) {
        filtered = filtered.filter(bill => 
          bill.patientName.toLowerCase().includes(departmentSearch.toLowerCase()) ||
          bill.department.toLowerCase().includes(departmentSearch.toLowerCase()) ||
          bill.opNumber.toLowerCase().includes(departmentSearch.toLowerCase()) ||
          bill.invoiceNo.toString().includes(departmentSearch.toLowerCase())
        );
      }
    } else {
      // Filter by OP number
      if (opNumberSearch) {
        filtered = filtered.filter(bill => 
          bill.opNumber.toLowerCase().includes(opNumberSearch.toLowerCase())
        );
      }
    }

    setFilteredBills(filtered);
  }, [selectedDepartment, departmentSearch, opNumberSearch, searchBy, allBills]);

  const handleBillSelect = (billId) => {
    if (selectedBills.length === 1 && selectedBills[0] === billId) {
      setSelectedBills([]);
      setPaymentAmounts({});
      return;
    }

    const bill = allBills.find(b => b.id === billId);
    setSelectedBills([billId]);
    setPaymentAmounts(bill ? { [billId]: bill.amount } : {});
  };

  const calculateTotal = () => {
    return selectedBills.reduce((total, billId) => {
      const bill = allBills.find(b => b.id === billId);
      return total + (bill ? bill.amount : 0);
    }, 0);
  };

  // Verify that selected bills are still pending before processing payment
  const verifyBillsArePending = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_PENDING_BILLS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'EntryDate': selectedDate,
          'FromDate': fromDate,
          'opNumber': '',
        },
      });

      if (response.status === 200) {
        // Process the fresh API data to get current pending bills
        const processedBills = [];
        const billIdSet = new Set();
        
        response.data.data.forEach(departmentData => {
          departmentData.bills.forEach(bill => {
            const uniqueId = `${bill.invoiceNo}-${bill.transType}-${bill.sequence || 0}`;
            if (!billIdSet.has(uniqueId)) {
              billIdSet.add(uniqueId);
              processedBills.push({
                id: uniqueId,
                invoiceNo: bill.invoiceNo,
                transType: bill.transType,
                sequence: bill.sequence,
                balanceDue: bill.balanceDue
              });
            }
          });
        });

        // Check if all selected bills are still in the pending list
        const missingBills = [];
        const billsWithChangedAmount = [];
        
        selectedBills.forEach(billId => {
          const selectedBill = allBills.find(b => b.id === billId);
          if (!selectedBill) {
            missingBills.push(billId);
            return;
          }

          const pendingBill = processedBills.find(b => b.id === billId);
          
          if (!pendingBill) {
            // Bill is no longer pending (might be paid or cancelled)
            missingBills.push({
              id: billId,
              invoiceNo: selectedBill.invoiceNo,
              opNumber: selectedBill.opNumber,
              patientName: selectedBill.patientName
            });
          } else {
            // Check if the balance due has changed
            const currentBalance = parseFloat(pendingBill.balanceDue);
            const originalBalance = parseFloat(selectedBill.amount);
            
            if (Math.abs(currentBalance - originalBalance) > 0.01) {
              billsWithChangedAmount.push({
                id: billId,
                invoiceNo: selectedBill.invoiceNo,
                opNumber: selectedBill.opNumber,
                patientName: selectedBill.patientName,
                originalAmount: originalBalance,
                currentAmount: currentBalance
              });
            }
          }
        });

        // Return verification result
        return {
          isValid: missingBills.length === 0 && billsWithChangedAmount.length === 0,
          missingBills,
          billsWithChangedAmount
        };
      }
      
      return { isValid: false, missingBills: [], billsWithChangedAmount: [] };
    } catch (error) {
      console.error('Error verifying bills:', error);
      throw error;
    }
  };

  const handlePayment = async () => {
    if (selectedBills.length === 0) {
      toast.error('Please select a bill to proceed with payment.');
      return;
    }

    if (calculatePaymentTotal() === 0) {
      toast.error('Please enter payment amounts for selected bills.');
      return;
    }

    if (printBillAfterSave && isElectron() && !sysInfo?.pharmacyBillPriinter) {
      toast.error('Cannot print bill. Pharmacy bill printer is not selected.', { autoClose: 2000 });
      return;
    }

    try {
      setLoading(true);
      
      // Verify bills are still pending before processing payment
      const verification = await verifyBillsArePending();
      
      if (!verification.isValid) {
        setLoading(false);
        
        // Handle missing bills (no longer pending)
        if (verification.missingBills.length > 0) {
          const billDetails = verification.missingBills.map(bill => 
            typeof bill === 'object' 
              ? `${bill.opNumber} (${bill.patientName})`
              : bill
          ).join(', ');
          
          toast.error(
            `Cannot process payment. Some bills are no longer pending: ${billDetails}. Please refresh and try again.`,
            { autoClose: 4000 }
          );
          
          // Close modal, clear selections, and refresh bills
          setShowPaymentModal(false);
          setSelectedBills([]);
          setPaymentAmounts({});
          await getData(selectedDate);
          return;
        }
        
        // Handle bills with changed amounts
        if (verification.billsWithChangedAmount.length > 0) {
          const billDetails = verification.billsWithChangedAmount.map(bill => 
            `${bill.opNumber}: ₹${bill.originalAmount.toLocaleString()} → ₹${bill.currentAmount.toLocaleString()}`
          ).join(', ');
          
          toast.warning(
            `Bill amounts have changed: ${billDetails}. Please refresh and verify amounts before proceeding.`,
            { autoClose: 4000 }
          );
          
          // Close modal, clear selections, and refresh bills
          setShowPaymentModal(false);
          setSelectedBills([]);
          setPaymentAmounts({});
          await getData(selectedDate);
          return;
        }
      }
      
      // Generate receipt data for API
      const receiptData = generateReceiptData();

      const receiptDetails = generateReceiptDetails();
      
      
      // TODO: Replace with your actual payment API endpoint
      const response = await axios.post(`${BASE_URL}${POST_PAYMENT}`, receiptData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Department': 'Pharmacy',
          'UserID': userInfo?.UserID,
        },
      });

      const receipt_number =response.data.data[0].ReceiptNo

      if(response.status === 200){
        toast.success(`Payment of ₹${calculatePaymentTotal().toLocaleString()} marked successfully!`, {
          autoClose: 2000
        });

        setReceiptData({
          ...receiptDetails,
          receiptNumber: receipt_number,
          paymentMethod: paymentMethod
        });

        const billsForPrint = selectedBills
          .map((billId) => allBills.find((b) => b.id === billId))
          .filter(Boolean);

        if (printAfterSave || printBillAfterSave) {
          setTimeout(async () => {
            try {
              if (printAfterSave) {
                await new Promise((resolve) => handlePrint(resolve));
              }
              if (printBillAfterSave) {
                await handlePrintBills(billsForPrint);
              }
            } catch (printError) {
              console.error('Post-payment print error:', printError);
              toast.error('Payment saved, but printing failed.', { autoClose: 2000 });
            } finally {
              resetAfterPayment();
            }
          }, 500);
        } else {
          resetAfterPayment();
        }
        
      } else {
        toast.success('Something went wrong', {
          autoClose: 2000
        });
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error('Error processing payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  const getDepartmentIcon = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    if (dept) return dept.icon;
    return departmentId?.toLowerCase()?.includes('ip') ? FaStethoscope : FaReceipt;
  };

  const getDepartmentColor = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    if (dept) return dept.bgColor;
    return departmentId?.toLowerCase()?.includes('ip') ? 'bg-blue-50' : 'bg-green-50';
  };

  const getDepartmentName = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    return dept ? dept.name : (departmentId || 'Unknown Department');
  };

  const getDepartmentTextColor = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    if (dept) return dept.textColor;
    return departmentId?.toLowerCase()?.includes('ip') ? 'text-blue-700' : 'text-green-700';
  };

  // State for payment amounts
  const [paymentAmounts, setPaymentAmounts] = useState({});

  // Initialize payment amounts when modal opens
  const initializePaymentAmounts = () => {
    if (selectedBills.length > 0) {
      const amounts = {};
      selectedBills.forEach(billId => {
        const bill = allBills.find(b => b.id === billId);
        if (bill) {
          amounts[billId] = bill.amount; // Default to full amount
        }
      });
      setPaymentAmounts(amounts);
    }
  };

  // Initialize payment amounts when opening modal
  useEffect(() => {
    if (showPaymentModal && selectedBills.length > 0) {
      initializePaymentAmounts();
    }
  }, [showPaymentModal]);

  // Update payment amount for a specific bill
  const updatePaymentAmount = (billId, amount) => {
    const bill = allBills.find(b => b.id === billId);
    if (bill && amount >= 0 && amount <= bill.amount) {
      setPaymentAmounts(prev => ({
        ...prev,
        [billId]: parseFloat(amount) || 0
      }));
    }
  };

  // Calculate total payment amount
  const calculatePaymentTotal = () => {
    return Object.values(paymentAmounts).reduce((total, amount) => total + amount, 0);
  };

  // Generate receipt data for API
  const generateReceiptData = () => {
    const selectedBillsData = selectedBills.map(billId => {
      const bill = allBills.find(b => b.id === billId);
      const payingAmount = paymentAmounts[billId] || 0;
      return {
        invoiceNo: parseInt(bill.invoiceNo),
        sequence: parseInt(bill.sequence) || 2024,
        editNo: parseInt(bill.editNo),
        totalAmount: parseFloat(bill.totalAmount).toFixed(2),
        payingAmout: parseFloat(payingAmount).toFixed(2),
        transType: bill.transType
      };
    });
  
    // Check if multiple patients are selected
    const uniquePatients = [...new Set(selectedBills.map(billId => {
      const bill = allBills.find(b => b.id === billId);
      return bill.patientId;
    }))];
  
    const isMultiplePatients = uniquePatients.length > 1;
  
    // Determine payment amounts based on payment method
    const totalPayingAmount = calculatePaymentTotal();
    let cashAmount = 0;
    let bankAmount = 0;
  
    if (paymentMethod === 'Cash') {
      cashAmount = totalPayingAmount;
      bankAmount = 0;
    } else {
      cashAmount = 0;
      bankAmount = totalPayingAmount;
    }
  
    return {
      patientID: isMultiplePatients ? -1 : parseInt(uniquePatients[0]),
      patientName: isMultiplePatients ? "multiMode" : allBills.find(b => selectedBills.includes(b.id))?.patientName || "Unknown",
      cashAmount: parseFloat(cashAmount).toFixed(2),
      bankAmount: parseFloat(bankAmount).toFixed(2),
      payMode: paymentMethod,
      drAmount: 0, // You can set this based on your business logic
      crAmount: 0,
      lines: selectedBillsData
    };
  };

  const printKOT = async (onComplete) => {
    const finish = onComplete || (() => {});
    if (!isElectron()) {
      // Cloud: browser print, then always complete so payment reset can run
      try {
        webPrintElementById('receiptDiv', { title: 'Payment Receipt' });
      } catch (err) {
        console.error('Web KOT print failed:', err);
      } finally {
        finish();
      }
      return;
    }

    if(!sysInfo.opRecieptPrinter){
      toast.error('Cannot Print,Printer Not Selected ', { duration: 1000 });
      return;
    }

    const receiptDiv = document.getElementById('receiptDiv');
    
    if (!receiptDiv) {
      toast.error('KOT element not found', { duration: 1000 });
      return;
    }

    try {
      // Function to convert image to base64
      const getBase64Image = (imgPath) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          };
          img.onerror = reject;
          img.src = imgPath;
        });
      };

      // Convert the QR code image to base64
      let base64Image = '';
      try {
        base64Image = await getBase64Image(`${process.env.PUBLIC_URL}/general.png`);
      } catch (error) {
        console.warn('Failed to convert image to base64:', error);
      }

      // Clone the KOT div and replace the image source
      const kotClone = receiptDiv.cloneNode(true);
      const imgElement = kotClone.querySelector('img');
      if (imgElement && base64Image) {
        imgElement.src = base64Image;
      }

      // Get the HTML content with base64 image
      const kotHTML = kotClone.outerHTML;
      
      // Enhanced CSS for printing with better image handling
      const printCSS = `
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Courier Prime', 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.2;
            color: #000;
            background: #fff;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }
          
          #receiptDiv {
            width: 302px;
            margin: 0 auto;
            padding: 8px;
            background: #fff;
            color: #000;
            font-family: 'Courier Prime', 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.2;
          }
          
          .text-xs { font-size: 11px; }
          .text-sm { font-size: 13px; }
          .text-md { font-size: 15px}
          .text-lg { font-size: 14px; }
          .font-bold { font-weight: 700; }
          .font-extrabold { font-weight: 800; }

          .w-16 { 
            width: 16px !important; 
            max-width: 16px !important;
          }
          .h-16 { 
            height: 16px !important; 
            max-height: 16px !important;
          }
          
          /* Enhanced image styling for printing */
          img {
            width: 70px !important;
            height: 70px !important;
            max-width: 70px !important;
            max-height: 70px !important;
            display: block !important;
            margin: 0 auto !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          
          .flex { display: flex; }
          .justify-between { justify-content: space-between; }
          .flex-1 { flex: 1; }
          .items-center { align-items: center; }
          
          .mb-1 { margin-bottom: 4px; }
          .mb-2 { margin-bottom: 8px; }
          .mb-3 { margin-bottom: 12px; }
          .my-2 { margin: 8px 0; }
          .py-1 { padding: 4px 0; }
          .pt-2 { padding-top: 8px; }
          .pr-2 { padding-right: 8px; }
          .ml-4 { margin-left: 16px; }
          
          .border-t { border-top: 1px solid #000; }
          .border-b { border-bottom: 1px solid #000; }
          .border-dashed { border-style: dashed; }
          .border-black { border-color: #000; }
          
          .leading-tight { line-height: 1.1; }
          .whitespace-nowrap { white-space: nowrap; }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            #receiptDiv {
              width: 302px !important;
              margin: 0 !important;
              padding: 8px !important;
            }
            
            img {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      `;

      // Set up the listener BEFORE sending the event
      ipcRenderer.once('kot-print-success', async (event, { status, error }) => {
        if (status === 'success') {
          finish();
          toast.success('KOT printed successfully!', { duration: 1000 });
        } else {
          console.error('KOT Print failed:', error);
          toast.error('KOT Print failed. Please check your printer settings.', {
            autoClose: 1000,
          });
          setLoading(false);
        }
      });

      // Send the HTML content for direct printing
      ipcRenderer.send('print-kot-html', {
        htmlContent: kotHTML,
        cssContent: printCSS,
        printer: sysInfo.opRecieptPrinter,
      });

    } catch (error) {
      console.error('Failed to prepare KOT for printing:', error);
      toast.error('Failed to prepare KOT for printing.', {
        autoClose: 1000,
      });
    }
  };

  
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const resetAfterPayment = () => {
    setShowPaymentModal(false);
    setSelectedBills([]);
    setPaymentAmounts({});
    getData(selectedDate);
    setLoading(false);
    setReceiptData(null);
  };

  const fetchInvoiceDetailsForPrint = async (invoiceNo, sequence) => {
    const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
      headers: {
        'XApiKey': process.env.REACT_APP_API_KEY,
        'Authorization': `Bearer ${token}`,
      },
      params: {
        Sequence: sequence || localStorage.getItem('finac_year'),
        InvoiceNo: invoiceNo.toString(),
      }
    });

    if (response.status !== 200) {
      throw new Error('Failed to fetch invoice details');
    }

    const invoiceData = response.data.data;
    if (!invoiceData?.details || !invoiceData?.header) {
      throw new Error('Invoice details not found');
    }

    return {
      header: invoiceData.header,
      items: transformInvoiceItems(invoiceData.details, invoiceData.itemList || []),
    };
  };

  const buildBillPrintData = (header, items) => {
    const isGstBill = (generalsettings?.phIsGstBill || 'No').toLowerCase() === 'yes';
    const discount = parseFloat(header.DiscountAmount || 0);
    const subTotal = parseFloat(header.NetAmount || 0)
      + parseFloat(header.TaxAmount || 0)
      + discount;
    // Net Amount = Sub Total - Discount (printer uses `balance` for this label)
    const netBeforeRound = subTotal - discount;
    const netAmount = generalsettings?.phValueRoundOff
      ? roundPrintAmount(netBeforeRound)
      : Number(netBeforeRound.toFixed(2));
    const balanceDue = parseFloat(header.BalanceDue || 0);

    return {
    settings: {
      cpi: 12,
      width: generalsettings?.phPrinterWidth || 80,
      indent: 0,
      printerType: generalsettings?.phPrinterType || "TVS",
      feedInLines: generalsettings?.phFeedinLines || 0,
      feedInForward: generalsettings?.phFeedInForward || 0,
      feedOutLines: generalsettings?.phFeedOutLines || 0,
      feedOutForward: generalsettings?.phFeedOutForward || 0,
      returnValidityDays: generalsettings?.phReturnValidityDays || 0,
      is_gstbill: generalsettings?.phIsGstBill || "No",
      show_itemwise_discount: generalsettings?.ShowItemwiseDiscount || "No",
    },
    header: {
      firmName:
        generalsettings?.phShowFirmname
          ? `${generalsettings?.FirstName || ""} ${generalsettings?.LastName || ""}`.trim()
          : "",
      phones:
        generalsettings?.phShowFirmnumber
          ? [generalsettings?.MobileNo1, generalsettings?.MobileNo2].filter(Boolean)
          : [],
      username:
        generalsettings?.phShowUsername && userInfo?.UserName
          ? String(userInfo.UserName)
          : "",
      address:
        generalsettings?.phShowAddress
          ? [generalsettings?.Place].filter(Boolean)
          : [],
      workingTime:
        generalsettings?.phShowWorkingHourse && generalsettings?.WorkingTime
          ? String(generalsettings.WorkingTime)
          : "",
      gst:
        isGstBill && generalsettings?.phShowGst && generalsettings?.GstNo
          ? String(generalsettings.GstNo)
          : "",
      invoiceNumber: formatPrintInvoiceNumber(header.InvoiceNo),
      date: formatBillPrintDate(header.CreatedDate || header.EntryDate),
      patientName: header?.PatientName || "",
      age: header?.Age ? String(header.Age) : "",
      gender: header?.Gender || "",
      doctor: header?.DoctorName || "",
      dlnum1: generalsettings?.dlnum1 || "",
      dlnum2: generalsettings?.dlnum2 || "",
      place: generalsettings?.Place || "",
    },
    products: items.map((p, i) => {
      let expFormatted = "";
      if (p?.ExpiryDate) {
        const expDate = String(p.ExpiryDate);
        if (expDate.includes('-')) {
          const parts = expDate.split('-');
          if (parts.length >= 2) {
            const year = parts[0].slice(-2);
            expFormatted = `${parts[1]}/${year}`;
          } else {
            expFormatted = expDate;
          }
        } else {
          expFormatted = expDate;
        }
      }

      return {
        sr: i + 1,
        name: String(p.ItemName || ""),
        batch: p?.BatchCode ? String(p.BatchCode).slice(-6) : "-",
        qty: Number(p.Qty ?? 0),
        rate: Number(p.SalesPrice ?? 0),
        mrp: Number(p.MRP ?? 0),
        ...(isGstBill && {
          gstRate: Number(p.TaxCode ?? 0),
          gstAmount: Number(p.TaxAmount ?? 0),
        }),
        discount: Number(p.Disc_Amount ?? 0),
        total: Number(p.PostTaxTotal ?? 0),
        grandTotal: Number(p.Total ?? 0),
        exp: expFormatted
      };
    }),
    totals: {
      totalAmount: Number(subTotal.toFixed(2)),
      grandTotal: Number(netAmount),
      discount: Number(discount),
      ...(isGstBill && { gst: Number(header.TaxAmount ?? 0) }),
      paid: Number(Number(netAmount) - balanceDue),
      // Printer maps `balance` → "Net Amount" on the slip (not remaining due)
      balance: Number(netAmount),
      balanceInWords: numberToWords(netAmount),
    }
  };
  };

  const printPharmacyBill = async (printData) => {
    const copies = getBillCopies(generalsettings);

    if (!isElectron()) {
      webPrintA5Receipt(printData, { title: 'PHARMACY CASH BILL', copies });
      return;
    }

    if (!sysInfo?.pharmacyBillPriinter) {
      toast.error('Cannot print bill. Pharmacy bill printer is not selected.', { autoClose: 2000 });
      return;
    }

    const phBillType = sysInfo?.phbilltype || generalsettings?.phBillType || '';

    await invokeBillPrint(ipcRenderer, {
      printerName: sysInfo.pharmacyBillPriinter,
      json: printData,
      phBillType,
      copies
    });
  };

  const handlePrintBills = async (billsToPrint = []) => {
    if (!billsToPrint.length) return;

    for (const bill of billsToPrint) {
      try {
        const { header, items } = await fetchInvoiceDetailsForPrint(bill.invoiceNo, bill.sequence);
        const printData = buildBillPrintData(header, items);
        await printPharmacyBill(printData);
      } catch (error) {
        console.error(`Failed to print bill ${bill.invoiceNo}:`, error);
        toast.error(`Failed to print bill #${bill.invoiceNo}`, { autoClose: 2000 });
      }
    }
  };

  const handlePrint = async (onComplete) => {
    const finish = onComplete || (() => {});

    if(generalsettings?.RecieptType === 'KOT'){
      await new Promise((resolve) => printKOT(resolve));
      return;
    }

    if(!isElectron()){
      // Cloud: browser print receipt, then complete so modal closes / list refreshes
      try {
        webPrintElementById('receiptDiv', { title: 'Payment Receipt' });
      } catch (err) {
        console.error('Web receipt print failed:', err);
      } finally {
        finish();
      }
      return;
    }

    if(!sysInfo.opRecieptPrinter){
      toast.error('Cannot Print,Printer Not Selected ', { duration: 1000 });
      return;
    }

    const receiptDiv = document.getElementById('receiptDiv');
    if (!receiptDiv) {
      return;
    }

    const promises = [];

    for (let i = 1; i <= totalPages; i++) {
      await new Promise((resolve) => {
        setCurrentPage(i);
        setTimeout(resolve, 600);
      });

      const dataUrl = await toPng(receiptDiv, { quality: 1, pixelRatio: 3 });
      promises.push(dataUrl);
    }

    await new Promise((resolve, reject) => {
      ipcRenderer.once('print-success', (event, { status, error }) => {
        if (status === 'success') {
          console.log('Print success:', status);
        } else {
          console.error('Print failed:', error);
          toast.error('Print failed. Please check your printer settings.', {
            autoClose: 1000,
          });
        }
        resolve();
      });

      Promise.all(promises)
        .then((images) => {
          images.forEach((imgDataUrl) => {
            ipcRenderer.send('print-bill-image', {
              imgDataUrl,
              printer: sysInfo.opRecieptPrinter,
            });
          });
          setTimeout(() => {
            finish();
            resolve();
          }, 1000);
        })
        .catch((error) => {
          toast.error('Failed to prepare pages for printing.', {
            autoClose: 1000,
          });
          reject(error);
        });
    });
  };

  const generateReceiptDetails = () => {
    const selectedBillsData = selectedBills.map(billId => {
      const bill = allBills.find(b => b.id === billId);
      const payingAmount = paymentAmounts[billId] || 0;
      return {
        ...bill,
        payingAmount: payingAmount,
        remainingAmount: bill.amount - payingAmount
      };
    });
  
    // Group by department and calculate totals
    const departmentTotals = selectedBillsData.reduce((acc, bill) => {
      if (!acc[bill.department]) {
        acc[bill.department] = {
          departmentName: bill.department,
          totalDue: 0,
          totalPaying: 0,
          totalRemaining: 0,
          billCount: 0,
          bills: []
        };
      }
      
      acc[bill.department].totalDue += bill.amount;
      acc[bill.department].totalPaying += bill.payingAmount;
      acc[bill.department].totalRemaining += bill.remainingAmount;
      acc[bill.department].billCount += 1;
      acc[bill.department].bills.push(bill);
      
      return acc;
    }, {});
  
    // Get unique patients
    const uniquePatients = [...new Set(selectedBillsData.map(bill => ({
      patientId: bill.patientId,
      patientName: bill.patientName
    })))];
  
    const isMultiplePatients = uniquePatients.length > 1;
  
    return {
      selectedBills: selectedBillsData,
      departmentTotals: Object.values(departmentTotals),
      totalPayingAmount: calculatePaymentTotal(),
      totalDueAmount: calculateTotal(),
      totalRemainingAmount: calculateTotal() - calculatePaymentTotal(),
      billCount: selectedBills.length,
      patients: uniquePatients,
      isMultiplePatients: isMultiplePatients,
      paymentDate: selectedDate
    };
  };
  


  return (
    <div className='flex h-full bg-background'>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      {loading && <Spinner />}
      
      <div className='flex-1 flex h-full flex-col overflow-hidden hide-scrollbar'>
        {/* Header */}
        <div className="bg-white p-3 mb-2">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                Payment Management
              </h1>
              <p className="text-xs text-black">Manage pending bills and process payments</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Export */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowDownloadOptions((prev) => !prev)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md shadow-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 transition-all"
                  title="Export pending bills"
                >
                  <FiDownload size={14} />
                  Export
                </button>

                {showDownloadOptions && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg overflow-hidden z-20">
                    <button
                      type="button"
                      onClick={() => handleDownload('excel')}
                      className="flex items-center w-full text-left px-4 py-3 hover:bg-gray-50 transition text-sm"
                    >
                      <FiFileText className="mr-2 text-green-600" /> Excel (.xlsx)
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload('pdf')}
                      className="flex items-center w-full text-left px-4 py-3 hover:bg-gray-50 transition text-sm"
                    >
                      <FiFileText className="mr-2 text-red-600" /> PDF Document
                    </button>
                  </div>
                )}
              </div>

              {/* Barcode Scanner Button */}
              <button 
                onClick={() => setShowScanModal(true)}
                className='px-3 py-1.5 text-xs font-medium rounded-md shadow-sm bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1.5 transition-all'
                title="Open Barcode Scanner"
              >
                <FiFileText size={14} />
                Scan Bills
                {scannedBarcodes.length > 0 && (
                  <span className="bg-green-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold">
                    {scannedBarcodes.length}
                  </span>
                )}
              </button>

              {selectedBills.length > 0 && (
                <button 
                  onClick={() => setShowPaymentModal(true)} 
                  className='px-4 py-1.5 text-xs font-medium rounded-md shadow-sm bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-md transition-all'
                >
                  Process Payment
                </button>
              )}
              
              {selectedBills.length > 0 && (
                <div className="px-3 py-1 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <span className="text-xs font-medium text-indigo-700">
                    Total: ₹{calculateTotal().toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Controls - Compact */}
        <div className="bg-white ml-3 mr-3 rounded-lg shadow-sm p-3 mb-2">
          <div className="w-full flex p-3 bg-gray-100 rounded-lg border border-gray-200 flex-col gap-2.5">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-300">
              <FiSearch className="text-indigo-600" size={16} />
              <h3 className="font-semibold text-black text-sm">Search & Filter</h3>
            </div>

            <div className="grid grid-cols-12 gap-2.5">
              {/* Search Mode Toggle */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-black mb-1">
                  Search Mode
                </label>
                <div className="flex gap-1">
                  <button
                    className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      searchBy === 'department' 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-gray-400 bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => setSearchBy('department')}
                  >
                    <FiFilter className="inline mr-1" size={12} />
                    Dept
                  </button>
                  <button
                    className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      searchBy === 'opnumber' 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-gray-400 bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => setSearchBy('opnumber')}
                  >
                    <FiUser className="inline mr-1" size={12} />
                    OP No
                  </button>
                </div>
              </div>

              {/* From Date */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-black mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  max={selectedDate}
                  onChange={handleFromDateChange}
                  className="w-full px-2 py-1.5 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                />
              </div>

              {/* To Date (EntryDate) */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-black mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  min={fromDate}
                  onChange={handleDateChange}
                  className="w-full px-2 py-1.5 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                />
              </div>

              {/* Search Input */}
              <div className="col-span-6">
                <label className="block text-xs font-medium text-black mb-1">
                  {searchBy === 'opnumber' ? 'OP Number' : 'Search'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-gray-400">
                    <FiSearch size={14} />
                  </div>
                  {searchBy === 'opnumber' ? (
                    <input
                      type="text"
                      value={opNumberSearch}
                      onChange={(e) => setOpNumberSearch(e.target.value)}
                      placeholder="Enter OP Number (e.g., GB2024/8)"
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    />
                  ) : (
                    <input
                      type="text"
                      ref={searchOpnumRef}
                      value={departmentSearch}
                      onChange={(e) => setDepartmentSearch(e.target.value)}
                      placeholder="Search by patient name, invoice number..."
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Department Filter - Compact */}
        {searchBy === 'department' && (
          <div className="bg-white ml-3 mr-3 rounded-lg shadow-sm p-3 mb-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FiShield className="text-indigo-600" size={16} />
                <h3 className="font-semibold text-black text-sm">Choose Department</h3>
              </div>
              <div className='flex items-center gap-2'>
                <button 
                  className='p-1.5 bg-white text-gray-600 rounded-lg hover:bg-gray-50 hover:text-indigo-600 shadow-sm transition-colors border border-gray-400' 
                  onClick={() => getData(selectedDate, fromDate)}
                  title="Refresh"
                >
                  <TbRefresh size={16}/>
                </button>
                <span className="text-xs text-black">{departments.length} departments</span>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                className={`flex-shrink-0 p-2 rounded-lg border transition-all min-w-[140px] ${
                  selectedDepartment === 'all'
                    ? 'border-indigo-500 bg-indigo-50 shadow-md'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-sm'
                }`}
                onClick={() => setSelectedDepartment('all')}
              >
                <div className="text-left">
                  <p className="font-semibold text-black flex items-center text-xs mb-0.5">
                    <FiShield className="mr-1.5" size={12} />
                    All Departments
                  </p>
                  <p className="text-xs text-gray-600">
                    {allBills.length} bills
                  </p>
                </div>
              </button>

              {departments.map(department => {
                const IconComponent = department.icon;
                return (
                  <button
                    key={department.id}
                    className={`flex-shrink-0 p-2 rounded-lg border transition-all min-w-[140px] ${
                      selectedDepartment === department.id 
                        ? `border-indigo-500 ${department.bgColor} shadow-md` 
                        : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedDepartment(department.id)}
                  >
                    <div className="text-left">
                      <p className="font-semibold text-black flex items-center text-xs mb-0.5">
                        <IconComponent className="mr-1.5" size={12} />
                        {department.name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {department.count} bills
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bills Grid */}
        <div className="mt-2 flex-1 overflow-hidden flex flex-col ml-3 mr-3">
          <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-black">
            <h3 className="font-semibold text-black text-sm">Pending Bills</h3>
            <span className="text-xs text-black">{filteredBills.length} bills found</span>
          </div>
          
          <div className="overflow-y-auto flex-1">
            {filteredBills.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5">
                {filteredBills.map(bill => {
                  const IconComponent = getDepartmentIcon(bill.department);
                  const isSelected = selectedBills.includes(bill.id);
                  
                  return (
                    <div
                      key={bill.id}
                      className={`relative bg-white rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md cursor-pointer ${
                        isSelected 
                          ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => handleBillSelect(bill.id)}
                    >
                      <div className="p-2.5">
                        {/* Department Header */}
                        <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mb-1.5 text-black ${getDepartmentColor(bill.department)} ${getDepartmentTextColor(bill.department)}`}>
                          <IconComponent size={10} className="mr-1" />
                          {getDepartmentName(bill.department)}
                        </div>

                        {/* Patient Info */}
                        <div className="mb-1.5">
                          <h3 className="font-semibold text-black text-xs mb-0.5 truncate">{bill.patientName}</h3>
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs bg-gray-100 text-gray-800 px-1 rounded truncate flex-1">
                              {bill.opNumber}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-1 text-xs text-gray-600 mt-0.5">
                            <span>Inv: {bill.invoiceNo}</span>
                            {bill.entryDate ? (
                              <span className="whitespace-nowrap">{formatBillEntryDate(bill.entryDate)}</span>
                            ) : null}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="pt-1 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-red-600 font-medium">Due</span>
                            <span className="font-bold text-sm text-red-600">
                              ₹{bill.amount.toLocaleString()}
                            </span>
                          </div>
                          {bill.totalAmount !== bill.amount && (
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-xs text-gray-600">Total</span>
                              <span className="text-xs text-gray-600">
                                ₹{bill.totalAmount.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Selection Indicator */}
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5 bg-indigo-600 text-white rounded-full p-0.5">
                            <FiCheck size={10} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-black py-8 border border-gray-300 rounded-lg bg-white">
                <FiFileText size={32} className="mx-auto mb-3 text-gray-300" />
                <h3 className="text-sm font-medium mb-1">No bills found</h3>
                <p className="text-xs text-gray-600">
                  {searchBy === 'opnumber' 
                    ? 'Try searching with a different OP number' 
                    : 'No pending bills in the selected department'
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Barcode Scan Modal */}
        {showScanModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={closeScanModal}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <FiFileText className="mr-2 text-indigo-600" />
                  Scanned Barcodes
                </h2>
                <button
                  onClick={closeScanModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Close and clear all scans"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Scan one bill barcode, then press <kbd className="px-2 py-0.5 bg-gray-200 rounded text-xs font-mono">Enter</kbd> to load it.
                </p>

                {scannedBarcodes.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                    {scannedBarcodes.map((barcode, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center">
                          <div className="bg-indigo-100 text-indigo-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm mr-3">
                            {index + 1}
                          </div>
                          <span className="font-mono text-sm font-medium text-gray-800">
                            Invoice: {barcode}
                          </span>
                        </div>
                        <button
                          onClick={() => removeScannedBarcode(barcode)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1"
                          title="Remove"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <FiFileText size={48} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No barcodes scanned yet</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={clearAllScannedBarcodes}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Clear All
                </button>
                <button
                  onClick={loadScannedBarcodes}
                  disabled={scannedBarcodes.length === 0}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                    scannedBarcodes.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  Load Bill
                </button>
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-800 mb-2">
                  <span className="font-semibold">💡 Workflow:</span>
                </p>
                <ol className="text-xs text-blue-700 space-y-1 ml-4 list-decimal">
                  <li>Scan one bill barcode</li>
                  <li>Press <kbd className="px-1.5 py-0.5 bg-white rounded border border-blue-300 font-mono text-xs mx-1">Enter</kbd> to load the bill</li>
                  <li>Payment modal opens automatically</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {showPaymentModal && (
          <PaymentModal 
            showPaymentModal={showPaymentModal}
            setShowPaymentModal={setShowPaymentModal}
            selectedBills={selectedBills}
            allBills={allBills}
            paymentAmounts={paymentAmounts}
            setPaymentAmounts={setPaymentAmounts}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            calculateTotal={calculateTotal}
            calculatePaymentTotal={calculatePaymentTotal}
            updatePaymentAmount={updatePaymentAmount}
            handlePayment={handlePayment}
            printAfterSave={printAfterSave}
            setPrintAfterSave={setPrintAfterSave}
            printBillAfterSave={printBillAfterSave}
            setPrintBillAfterSave={setPrintBillAfterSave}
          />
        )}

        {generalsettings?.RecieptTemplate === 'A5' ? (
          <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          {/* <div> */}
            <ReceiptNormalComponent
              selectedDate={selectedDate}
              sysInfo={sysInfo}
              clientInfo={clientInfo}
              generalsettings={generalsettings}
              receiptData={receiptData} // Pass receipt data
              username={userInfo?.UserName || 'Admin'}
            />
          </div>
        ) : (
          <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          {/* <div> */}
            <ReceiptKotComponent
              selectedDate={selectedDate}
              sysInfo={sysInfo}
              clientInfo={clientInfo}
              generalsettings={generalsettings}
              receiptData={receiptData} // Pass receipt data
              username={userInfo?.UserName || 'Admin'}
            />
          </div>
        )}

        
      </div>
    </div>
  );
};

export default PaymentHandlingPage;